import { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { enabledDomains, firstEnabledDomain, isDomainPathEnabled } from "@/router/domain-flags";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  module: string;
  subItems?: { path: string; label: string }[];
  badge?: number;
  group?: string;
}

interface SidebarProps {
  onClose?: () => void;
}

const moduleOptions = [
  ...(__DOMAIN_CRYPTO__ ? [{ key: "crypto" as const, label: "币圈", icon: "ri-coin-line", color: "bg-brand" }] : []),
  ...(__DOMAIN_US_STOCK__ ? [{ key: "us_stock" as const, label: "美股", icon: "ri-line-chart-line", color: "bg-emerald-500" }] : []),
  ...(__DOMAIN_FOREX__ ? [{ key: "forex" as const, label: "外汇", icon: "ri-currency-line", color: "bg-teal-500" }] : []),
  ...(__DOMAIN_AI__ ? [{ key: "ai_image" as const, label: "AI 生图", icon: "ri-image-ai-line", color: "bg-rose-500" }] : []),
];

const moduleDomainEnabled: Record<string, boolean> = {
  crypto: enabledDomains.crypto,
  us_stock: enabledDomains.usStock,
  forex: enabledDomains.forex,
  ai_image: enabledDomains.ai,
};

const moduleHome: Record<string, string> = {
  ...(__DOMAIN_CRYPTO__ ? { crypto: "/dashboard" } : {}),
  ...(__DOMAIN_US_STOCK__ ? { us_stock: "/us-stock" } : {}),
  ...(__DOMAIN_FOREX__ ? { forex: "/forex/overview" } : {}),
  ...(__DOMAIN_AI__ ? { ai_image: "/ai/reports" } : {}),
};

const cryptoNav: NavItem[] = __DOMAIN_CRYPTO__ ? [
  { path: "/dashboard", label: "仪表盘", icon: "ri-dashboard-3-line", module: "dashboard" },
  { path: "/users", label: "用户管理", icon: "ri-user-settings-line", module: "users" },
  { path: "/agents", label: "Agent 管理", icon: "ri-robot-2-line", module: "agents" },
  {
    path: "/settlements",
    label: "结算管理",
    icon: "ri-exchange-dollar-line",
    module: "settlements",
    subItems: [
      { path: "/settlements", label: "结算总览" },
      { path: "/settlement-records", label: "结算记录" },
      { path: "/deposits", label: "充值记录" },
      { path: "/payment-addresses", label: "收款地址" },
      { path: "/refunds", label: "退款管理" },
      { path: "/revenue-ledger", label: "收益台账" },
    ],
  },
  {
    path: "/withdrawals",
    label: "提现审核",
    icon: "ri-wallet-3-line",
    module: "withdrawals",
    subItems: [
      { path: "/withdrawals", label: "待审核" },
      { path: "/withdrawal-history", label: "提现历史" },
    ],
  },
  {
    path: "/risk-dashboard",
    label: "风控中心",
    icon: "ri-shield-check-line",
    module: "risk",
    subItems: [
      { path: "/risk-dashboard", label: "风控仪表盘" },
      { path: "/audit-logs", label: "审计日志" },
      { path: "/reconciliation", label: "链上对账" },
    ],
  },
  {
    path: "/config-rates",
    label: "系统配置",
    icon: "ri-settings-3-line",
    module: "config",
    subItems: [
      { path: "/config-rates", label: "费率配置" },
      { path: "/config-plans", label: "订阅计划" },
      { path: "/config-ai-models", label: "AI 模型配置" },
      { path: "/config-announcements", label: "系统公告" },
    ],
  },
] : [];

const usStockNav: NavItem[] = __DOMAIN_US_STOCK__ ? [
  { path: "/us-stock", label: "美股概览", icon: "ri-line-chart-line", module: "us_stock" },
] : [];

const forexNav: NavItem[] = __DOMAIN_FOREX__ ? [
  { path: "/forex/overview", label: "运营总览", icon: "ri-dashboard-line", module: "forex", group: "总览" },
  { path: "/forex/profit-dashboard", label: "盈利表现大盘", icon: "ri-funds-line", module: "forex" },
  { path: "/forex/model-dashboard", label: "模型调用大盘", icon: "ri-brain-line", module: "forex" },
  { path: "/forex/exceptions", label: "异常与待办", icon: "ri-alert-line", module: "forex", badge: 17 },
  { path: "/forex/agents", label: "智能体列表", icon: "ri-robot-2-line", module: "forex", group: "智能体运营" },
  { path: "/forex/agents/create", label: "创建智能体", icon: "ri-add-circle-line", module: "forex" },
  { path: "/forex/merchants", label: "运营归属管理", icon: "ri-store-3-line", module: "forex" },
  { path: "/forex/agents/audit", label: "上架审核", icon: "ri-file-list-3-line", module: "forex", badge: 3 },
  { path: "/forex/strategy-templates", label: "策略模板", icon: "ri-layout-column-line", module: "forex" },
  { path: "/forex/decision-templates", label: "AI 决策配置", icon: "ri-brain-line", module: "forex" },
  { path: "/forex/decision-flow/detail", label: "决策流程模板", icon: "ri-flow-chart", module: "forex" },
  { path: "/forex/agents/dry-run", label: "试跑记录", icon: "ri-test-tube-line", module: "forex" },
  { path: "/forex/model-config", label: "原子模型资源管理", icon: "ri-cpu-line", module: "forex" },
  { path: "/forex/master-accounts", label: "主账号管理", icon: "ri-server-line", module: "forex", group: "账号与终端" },
  { path: "/forex/terminal", label: "Terminal / EA", icon: "ri-terminal-box-line", module: "forex" },
  { path: "/forex/sub-accounts", label: "子账号审核", icon: "ri-user-add-line", module: "forex", badge: 12 },
  { path: "/forex/follow", label: "跟单监控", icon: "ri-git-branch-line", module: "forex", group: "跟单与执行" },
  { path: "/forex/copycmd", label: "CopyCommand", icon: "ri-file-copy-line", module: "forex" },
  { path: "/forex/execution-detail", label: "执行结果详情", icon: "ri-file-chart-line", module: "forex" },
  { path: "/forex/failure-analysis", label: "失败/阻断分析", icon: "ri-bar-chart-box-line", module: "forex" },
  { path: "/forex/risk-config", label: "风控配置", icon: "ri-shield-check-line", module: "forex", group: "风控与配置" },
  { path: "/forex/kill-switch", label: "Kill Switch", icon: "ri-shut-down-line", module: "forex" },
  { path: "/forex/config", label: "配置中心", icon: "ri-settings-3-line", module: "forex" },
  { path: "/forex/compliance", label: "协议与合规", icon: "ri-file-shield-2-line", module: "forex" },
  { path: "/forex/query-policy", label: "查询策略", icon: "ri-search-eye-line", module: "forex" },
  { path: "/forex/data-sources", label: "系统已注册数据源清单", icon: "ri-database-2-line", module: "forex" },
  { path: "/forex/bc-loop", label: "B/C 闭环", icon: "ri-loop-right-line", module: "forex" },
] : [];

const aiNav: NavItem[] = __DOMAIN_AI__ ? [
  { path: "/ai/reports", label: "数据报表", icon: "ri-bar-chart-2-line", module: "ai_reports" },
  {
    path: "/ai/users",
    label: "用户管理",
    icon: "ri-user-settings-line",
    module: "ai_users",
    subItems: [
      { path: "/ai/users", label: "用户列表" },
      { path: "/ai/users/behavior", label: "用户行为" },
    ],
  },
  {
    path: "/ai/generations",
    label: "生成记录",
    icon: "ri-image-ai-line",
    module: "ai_generations",
    subItems: [
      { path: "/ai/generations", label: "生成列表" },
    ],
  },
  {
    path: "/ai/vip/packages",
    label: "VIP 管理",
    icon: "ri-vip-crown-line",
    module: "ai_vip",
    subItems: [
      { path: "/ai/vip/packages", label: "VIP 套餐" },
      { path: "/ai/vip/subscription-orders", label: "订阅订单" },
      { path: "/ai/orders", label: "全部订单" },
    ],
  },
  { path: "/ai/points-log", label: "积分流水", icon: "ri-coins-line", module: "ai_points" },
  {
    path: "/ai/audit/images",
    label: "内容安全",
    icon: "ri-shield-check-line",
    module: "ai_audit",
    subItems: [
      { path: "/ai/audit/images", label: "图片审核" },
      { path: "/ai/audit/prompts", label: "提示词审核" },
      { path: "/ai/audit/agent-trace", label: "Agent 追踪" },
    ],
  },
  { path: "/ai/config", label: "应用配置", icon: "ri-settings-3-line", module: "ai_config" },
] : [];

const navMap: Record<string, NavItem[]> = {
  ...(__DOMAIN_CRYPTO__ ? { crypto: cryptoNav } : {}),
  ...(__DOMAIN_US_STOCK__ ? { us_stock: usStockNav } : {}),
  ...(__DOMAIN_FOREX__ ? { forex: forexNav } : {}),
  ...(__DOMAIN_AI__ ? { ai_image: aiNav } : {}),
};

const enabledModuleOptions = moduleOptions.filter((module) => moduleDomainEnabled[module.key]);

/* ---- explicit forex group membership: path -> group name ---- */
const forexGroupConfig: Record<string, string[]> = __DOMAIN_FOREX__ ? {
  "总览": ["/forex/overview", "/forex/profit-dashboard", "/forex/model-dashboard", "/forex/exceptions"],
  "智能体运营": ["/forex/agents", "/forex/agents/create", "/forex/agents/audit", "/forex/strategy-templates", "/forex/decision-templates", "/forex/decision-flow/detail", "/forex/agents/dry-run", "/forex/model-config", "/forex/data-sources", "/forex/merchants"],
  "账号与终端": ["/forex/master-accounts", "/forex/terminal", "/forex/sub-accounts"],
  "跟单与执行": ["/forex/follow", "/forex/copycmd", "/forex/execution-detail", "/forex/failure-analysis"],
  "风控与配置": ["/forex/risk-config", "/forex/kill-switch", "/forex/config", "/forex/compliance", "/forex/query-policy", "/forex/bc-loop"],
} : {};

const GROUP_ORDER = __DOMAIN_FOREX__ ? ["总览", "智能体运营", "账号与终端", "跟单与执行", "风控与配置"] : [];

const pathToGroup: Record<string, string> = {};
for (const [gName, paths] of Object.entries(forexGroupConfig)) {
  for (const p of paths) {
    pathToGroup[p] = gName;
  }
}

/* ---- structured forex section type ---- */
type ForexSection =
  | { kind: "group"; name: string; items: NavItem[] }
  | { kind: "standalone"; item: NavItem };

export default function Sidebar({ onClose }: SidebarProps) {
  const { admin, logout, canAccess, currentModule, setModule } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModuleMenu, setShowModuleMenu] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const activeModule = moduleDomainEnabled[currentModule] ? currentModule : firstEnabledDomain();
  const navItems = navMap[activeModule] ?? cryptoNav;
  const visibleNavItems = navItems.filter((item) => canAccess(item.module) && isDomainPathEnabled(item.path));

  /* ---- forex: find which group current route belongs to ---- */
  const currentForexGroup = useMemo(() => {
    if (activeModule !== "forex") return null;
    if (pathToGroup[location.pathname]) return pathToGroup[location.pathname];
    for (const [path, group] of Object.entries(pathToGroup)) {
      if (location.pathname.startsWith(path + "/")) return group;
    }
    return null;
  }, [activeModule, location.pathname]);

  /* ---- forex: pre-process into structured sections ---- */
  const forexSections = useMemo((): ForexSection[] => {
    if (activeModule !== "forex") return [];
    const sections: ForexSection[] = [];
    const groupItemMap: Record<string, NavItem[]> = {};
    for (const item of visibleNavItems) {
      const g = pathToGroup[item.path];
      if (g) {
        if (!groupItemMap[g]) groupItemMap[g] = [];
        groupItemMap[g].push(item);
      } else {
        sections.push({ kind: "standalone", item });
      }
    }
    for (const gName of GROUP_ORDER) {
      if (groupItemMap[gName]) {
        sections.push({ kind: "group", name: gName, items: groupItemMap[gName] });
      }
    }
    return sections;
  }, [activeModule, visibleNavItems]);

  const toggleExpand = (path: string) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const activeModuleLabel = enabledModuleOptions.find((m) => m.key === activeModule)?.label ?? "管理后台";

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside className="w-[280px] h-screen bg-sidebar flex flex-col shrink-0 border-r border-surface-border overflow-hidden">
      {/* Logo + Module Switcher */}
      <div className="h-[80px] flex items-center px-6 border-b border-surface-border shrink-0">
        <button
          onClick={() => {
            navigate("/");
            if (onClose) onClose();
          }}
          className="flex items-center gap-3.5 flex-1 text-left cursor-pointer bg-transparent border-0 p-0"
        >
          <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center shrink-0">
            <i className="ri-bar-chart-grouped-line text-white text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-text-primary text-base font-semibold leading-tight">钜睿科技</h1>
            <p className="text-text-muted text-xs leading-tight mt-0.5">量化管理后台</p>
          </div>
        </button>
        <div className="relative ml-2">
          <button
            onClick={() => setShowModuleMenu((s) => !s)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-sidebar-hover transition-colors cursor-pointer"
          >
            <span className="text-xs font-medium text-brand">{activeModuleLabel}</span>
            <i className={`ri-arrow-down-s-line text-xs text-text-muted transition-transform ${showModuleMenu ? "rotate-180" : ""}`} />
          </button>
          {showModuleMenu && (
            <div className="absolute top-full right-0 mt-1 w-[140px] bg-white rounded-xl border border-surface-border py-1.5 z-50">
              {enabledModuleOptions.map((m) => {
                const hasAccess = canAccess(m.key);
                return (
                  <button
                    key={m.key}
                    onClick={() => {
                      if (hasAccess) {
                        setModule(m.key);
                        setShowModuleMenu(false);
                        navigate(moduleHome[m.key]);
                      }
                    }}
                    disabled={!hasAccess}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
                      activeModule === m.key
                        ? "bg-surface text-brand font-medium"
                        : hasAccess
                        ? "text-text-secondary hover:bg-surface"
                        : "text-text-muted opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md ${m.color} flex items-center justify-center shrink-0`}>
                      <i className={`${m.icon} text-white text-xs`} />
                    </div>
                    <span>{m.label}</span>
                    {!hasAccess && <span className="ml-auto text-[10px] text-text-muted">无权限</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto pb-safe">
        {/* === FOREX: structured sections === */}
        {activeModule === "forex" && forexSections.map((section) => {
          if (section.kind === "group") {
            const { name: groupName, items: groupItems } = section;
            const isCurrentGroup = groupName === currentForexGroup;

            return (
              <details key={groupName} open className="mb-3 list-none group" data-forex-group={groupName}>
                <summary
                  className={`w-full flex items-center gap-1.5 px-5 py-1.5 cursor-pointer transition-colors ${
                    isCurrentGroup ? "text-text-primary" : "text-text-secondary hover:text-text-primary"
                  } [&::-webkit-details-marker]:hidden [&::marker]:hidden`}
                >
                  <span className={`text-[11px] font-semibold uppercase tracking-[0.08em] flex-1 ${
                    isCurrentGroup ? "text-text-primary" : "text-text-muted"
                  }`}>
                    {groupName}
                  </span>
                  <span className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`text-xs transition-transform duration-200 group-open:rotate-180 ${
                      isCurrentGroup ? "text-text-secondary" : "text-text-muted"
                    } ri-arrow-down-s-line`} />
                  </span>
                </summary>

                <div className="forex-group-content space-y-0.5 mt-0.5">
                    {groupItems.map((gi) => {
                      const giActive = location.pathname === gi.path;
                      const giHasSub = !!gi.subItems;
                      const giIsExpanded = expanded[gi.path] || giActive;

                      return (
                        <div key={gi.path}>
                          <Link
                            to={gi.path}
                            onClick={(e) => {
                              if (giHasSub) {
                                e.preventDefault();
                                toggleExpand(gi.path);
                              } else {
                                handleLinkClick();
                              }
                            }}
                            className={`relative mx-3 flex items-center gap-3 px-3.5 py-2 rounded-xl text-[13px] transition-colors cursor-pointer ${
                              giActive
                                ? "bg-sidebar-active text-brand font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-4 before:rounded-r-full before:bg-brand"
                                : "text-text-secondary hover:bg-sidebar-hover hover:text-text-primary"
                            }`}
                          >
                            <i className={`${gi.icon} text-sm w-5 h-5 flex items-center justify-center`} />
                            <span className="flex-1">{gi.label}</span>
                            {giHasSub && (
                              <span className="w-4 h-4 flex items-center justify-center">
                                <i
                                  className={`ri-arrow-down-s-line text-xs transition-transform text-text-muted ${
                                    giIsExpanded ? "rotate-180" : ""
                                  }`}
                                />
                              </span>
                            )}
                            {!giHasSub && gi.badge !== undefined && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white shrink-0 bg-amber-500">
                                {gi.badge}
                              </span>
                            )}
                          </Link>

                          {giHasSub && giIsExpanded && (
                            <div className="ml-10 mr-3 mb-2 space-y-0.5">
                              {gi.subItems?.map((sub) => {
                                const subActive = location.pathname === sub.path;
                                return (
                                  <Link
                                    key={sub.path}
                                    to={sub.path}
                                    onClick={handleLinkClick}
                                    className={`relative block px-3 py-2 rounded-lg text-sm transition-colors ${
                                      subActive
                                        ? "bg-sidebar-active text-brand font-medium"
                                        : "text-text-muted hover:text-text-secondary"
                                    } ${subActive ? "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-3 before:rounded-r-full before:bg-brand" : ""}`}
                                  >
                                    {sub.label}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
              </details>
            );
          }

          // Standalone item (no group)
          const item = section.item;
          const active = isActive(item.path);
          const hasSub = !!item.subItems;
          const isExpanded = expanded[item.path] || active;

          return (
            <div key={item.path}>
              <Link
                to={item.path}
                onClick={(e) => {
                  if (hasSub) {
                    e.preventDefault();
                    toggleExpand(item.path);
                  } else {
                    handleLinkClick();
                  }
                }}
                className={`relative mx-3 mb-1 flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] transition-colors cursor-pointer ${
                  active
                    ? "bg-sidebar-active text-brand font-medium"
                    : "text-text-secondary hover:bg-sidebar-hover hover:text-text-primary"
                } ${active ? "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-5 before:rounded-r-full before:bg-brand" : ""}`}
              >
                <i className={`${item.icon} text-base w-5 h-5 flex items-center justify-center`} />
                <span className="flex-1">{item.label}</span>
                {hasSub && (
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform text-text-muted ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                )}
                {!hasSub && item.badge !== undefined && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white shrink-0 bg-amber-500">
                    {item.badge}
                  </span>
                )}
              </Link>

              {hasSub && isExpanded && (
                <div className="ml-10 mr-3 mb-2 space-y-0.5">
                  {item.subItems?.map((sub) => {
                    const subActive = location.pathname === sub.path;
                    return (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        onClick={handleLinkClick}
                        className={`relative block px-3 py-2 rounded-lg text-sm transition-colors ${
                          subActive
                            ? "bg-sidebar-active text-brand font-medium"
                            : "text-text-muted hover:text-text-secondary"
                        } ${subActive ? "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-3 before:rounded-r-full before:bg-brand" : ""}`}
                      >
                        {sub.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* === NON-FOREX: original rendering === */}
        {activeModule !== "forex" && visibleNavItems.map((item, idx) => {
          const active = isActive(item.path);
          const hasSub = !!item.subItems;
          const isExpanded = expanded[item.path] || active;

          const prevGroup = idx > 0 ? visibleNavItems[idx - 1]?.group : undefined;
          const showGroupLabel = item.group && item.group !== prevGroup;

          return (
            <div key={item.path}>
              {showGroupLabel && (
                <div className="mx-3 mt-3 mb-1 first:mt-0">
                  <span className="block px-3.5 py-1.5 text-[10px] font-semibold text-text-muted uppercase tracking-[0.15em]">
                    {item.group}
                  </span>
                </div>
              )}
              <Link
                to={item.path}
                onClick={(e) => {
                  if (hasSub) {
                    e.preventDefault();
                    toggleExpand(item.path);
                  } else {
                    handleLinkClick();
                  }
                }}
                className={`relative mx-3 mb-1 flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] transition-colors cursor-pointer ${
                  active
                    ? "bg-sidebar-active text-brand font-medium"
                    : "text-text-secondary hover:bg-sidebar-hover hover:text-text-primary"
                } ${active ? "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-5 before:rounded-r-full before:bg-brand" : ""}`}
              >
                <i className={`${item.icon} text-base w-5 h-5 flex items-center justify-center`} />
                <span className="flex-1">{item.label}</span>
                {hasSub && (
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform text-text-muted ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                )}
                {!hasSub && item.badge !== undefined && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white shrink-0 bg-amber-500">
                    {item.badge}
                  </span>
                )}
              </Link>

              {hasSub && isExpanded && (
                <div className="ml-10 mr-3 mb-2 space-y-0.5">
                  {item.subItems?.map((sub) => {
                    const subActive = location.pathname === sub.path;
                    return (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        onClick={handleLinkClick}
                        className={`relative block px-3 py-2 rounded-lg text-sm transition-colors ${
                          subActive
                            ? "bg-sidebar-active text-brand font-medium"
                            : "text-text-muted hover:text-text-secondary"
                        } ${subActive ? "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-3 before:rounded-r-full before:bg-brand" : ""}`}
                      >
                        {sub.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
